/**
 * Wizard Engine
 * Handles navigation logic for the Model Planner Wizard
 * Manages question flow, looping, and branching
 * Updated: Now accepts configuration as parameter (database-driven)
 */

import questionsConfig from '../config/modelPlannerQuestions.json';

export class WizardEngine {
  /**
   * Create a new WizardEngine instance
   * @param {Object} sectionsConfig - Configuration object (defaults to JSON for backward compatibility)
   */
  constructor(sectionsConfig = null) {
    // Use provided config or fall back to JSON import for backward compatibility
    this.sections = sectionsConfig || questionsConfig.sections;
  }

  /**
   * Get section configuration
   * @param {string} sectionKey - Section key (ponds, curb, walls, etc.)
   * @returns {Object} Section config
   */
  getSection(sectionKey) {
    return this.sections[sectionKey];
  }

  /**
   * Get all section keys in order
   * @returns {Array<string>} Section keys
   */
  getSectionKeys() {
    return Object.keys(this.sections);
  }

  /**
   * Get initial question for a section
   * @param {string} sectionKey - Section key
   * @returns {Object} Question object
   */
  getInitialQuestion(sectionKey) {
    const section = this.getSection(sectionKey);
    if (!section) return null;

    const questionId = section.initialQuestion;
    return section.questions[questionId];
  }

  /**
   * Get question by ID within a section
   * @param {string} sectionKey - Section key
   * @param {string} questionId - Question ID
   * @returns {Object} Question object
   */
  getQuestion(sectionKey, questionId) {
    const section = this.getSection(sectionKey);
    if (!section) return null;

    return section.questions[questionId];
  }

  /**
   * Get next question based on current state
   * @param {string} sectionKey - Current section
   * @param {string} currentQuestionId - Current question ID
   * @param {Object} answer - Answer data
   * @param {Object} loopState - Current loop state
   * @returns {Object} { questionId, loopState, action }
   */
  getNextQuestion(sectionKey, currentQuestionId, answer, loopState = null) {
    const currentQuestion = this.getQuestion(sectionKey, currentQuestionId);
    if (!currentQuestion) {
      return { questionId: null, loopState, action: 'error' };
    }

    // Handle special navigation commands
    if (answer._navigate) {
      const target = answer._navigate;

      // Handle LOOP_START (old style with count)
      if (target === 'LOOP_START') {
        const count = answer._count || 1;
        const newLoopState = {
          totalCount: count,
          currentIndex: 0,
          itemIds: [],
          loopStartQuestion: this.getLoopStartQuestion(sectionKey)
        };
        return {
          questionId: newLoopState.loopStartQuestion,
          loopState: newLoopState,
          action: 'loop_start'
        };
      }

      // Handle LOOP_START_SINGLE (new style - create one item at a time)
      if (target === 'LOOP_START_SINGLE') {
        const newLoopState = {
          totalCount: 1,
          currentIndex: 0,
          itemIds: [],
          loopStartQuestion: this.getLoopStartQuestion(sectionKey),
          singleItemMode: true // Flag to indicate one-at-a-time creation
        };
        return {
          questionId: newLoopState.loopStartQuestion,
          loopState: newLoopState,
          action: 'loop_start_single'
        };
      }

      // Handle LOOP_CONTINUE (add another item)
      if (target === 'LOOP_CONTINUE') {
        if (loopState) {
          const newIndex = loopState.currentIndex + 1;
          return {
            questionId: loopState.loopStartQuestion,
            loopState: {
              ...loopState,
              totalCount: newIndex + 1,
              currentIndex: newIndex
            },
            action: 'loop_continue_add'
          };
        }
        return { questionId: null, loopState: null, action: 'error' };
      }

      // Handle ADA_LOOP_START (old style with count)
      if (target === 'ADA_LOOP_START') {
        const count = answer._count || 1;
        const newLoopState = {
          totalCount: count,
          currentIndex: 0,
          itemIds: [],
          loopStartQuestion: 'ada_ramp_type',
          isADALoop: true
        };
        return {
          questionId: 'ada_ramp_type',
          loopState: newLoopState,
          action: 'ada_loop_start'
        };
      }

      // Handle ADA_LOOP_START_SINGLE (new style - create one at a time)
      if (target === 'ADA_LOOP_START_SINGLE') {
        const newLoopState = {
          totalCount: 1,
          currentIndex: 0,
          itemIds: [],
          loopStartQuestion: 'ada_ramp_type',
          isADALoop: true,
          singleItemMode: true
        };
        return {
          questionId: 'ada_ramp_type',
          loopState: newLoopState,
          action: 'ada_loop_start_single'
        };
      }

      // Handle ADA_LOOP_CONTINUE (add another ADA ramp)
      if (target === 'ADA_LOOP_CONTINUE') {
        if (loopState) {
          const newIndex = loopState.currentIndex + 1;
          return {
            questionId: 'ada_ramp_type',
            loopState: {
              ...loopState,
              totalCount: newIndex + 1,
              currentIndex: newIndex
            },
            action: 'ada_loop_continue_add'
          };
        }
        return { questionId: null, loopState: null, action: 'error' };
      }

      // Handle LOOP_END
      if (target === 'LOOP_END' || target === 'ADA_LOOP_END') {
        if (loopState) {
          const nextIndex = loopState.currentIndex + 1;
          if (nextIndex < loopState.totalCount) {
            // Continue to next item in loop
            return {
              questionId: loopState.loopStartQuestion,
              loopState: {
                ...loopState,
                currentIndex: nextIndex
              },
              action: 'loop_continue'
            };
          } else {
            // Loop complete
            return {
              questionId: null,
              loopState: null,
              action: target === 'ADA_LOOP_END' ? 'ada_loop_complete' : 'loop_complete'
            };
          }
        }
        return { questionId: null, loopState: null, action: 'loop_complete' };
      }

      // Handle SECTION_COMPLETE
      if (target === 'SECTION_COMPLETE') {
        return {
          questionId: null,
          loopState: null,
          action: 'section_complete'
        };
      }

      // Regular navigation to specific question
      return {
        questionId: target,
        loopState,
        action: 'navigate'
      };
    }

    // Default: use question's 'next' property
    if (currentQuestion.next) {
      return this.getNextQuestion(sectionKey, currentQuestionId, { _navigate: currentQuestion.next }, loopState);
    }

    return { questionId: null, loopState, action: 'end' };
  }

  /**
   * Get all question IDs reachable from a boolean question's conditional paths
   * Used to determine if a question is part of the onYes/onNo follow-up chain
   * @param {string} sectionKey - Section key
   * @param {string} booleanQuestionId - ID of the boolean question
   * @param {boolean} answer - The answer (true = onYes path, false = onNo path)
   * @returns {Array<string>} Array of question IDs in the conditional path
   */
  getConditionalPath(sectionKey, booleanQuestionId, answer) {
    const question = this.getQuestion(sectionKey, booleanQuestionId);
    if (!question || question.type !== 'boolean') {
      return [];
    }

    const path = [];
    const convergencePoint = question.onNo || question.next; // Where paths rejoin
    let currentQuestionId = answer ? question.onYes : question.onNo;

    // Walk the question chain until we hit the convergence point or end
    const maxDepth = 20; // Prevent infinite loops
    let depth = 0;

    while (currentQuestionId && currentQuestionId !== convergencePoint && depth < maxDepth) {
      path.push(currentQuestionId);

      const currentQ = this.getQuestion(sectionKey, currentQuestionId);
      if (!currentQ) break;

      // Move to next question in chain
      currentQuestionId = currentQ.next;
      depth++;
    }

    return path;
  }

  /**
   * Get all questions that are reachable when editing a specific question
   * This handles nested boolean questions and their conditional paths
   *
   * @param {string} sectionKey - The section identifier
   * @param {string} startQuestionId - The question being edited
   * @param {Object} currentData - Current form data (to evaluate boolean answers)
   * @returns {Set} Set of question IDs that should be accessible in edit mode
   */
  getAllowedQuestionsInEditMode(sectionKey, startQuestionId, currentData) {
    const allowedQuestions = new Set();
    const visited = new Set(); // Prevent infinite loops
    const maxDepth = 50; // Safety limit for deeply nested questions

    // Recursive function to walk the question tree
    const walkQuestions = (questionId, depth = 0) => {
      // Safety checks
      if (!questionId || depth >= maxDepth || visited.has(questionId)) {
        return;
      }

      visited.add(questionId);
      allowedQuestions.add(questionId);

      const question = this.getQuestion(sectionKey, questionId);
      if (!question) return;

      // If it's a grouped_form, also include all its fields
      if (question.type === 'grouped_form' && question.fields) {
        question.fields.forEach(field => {
          if (field.id) {
            allowedQuestions.add(field.id);
            // Recursively walk if the field has conditional paths
            walkQuestions(field.id, depth + 1);
          }
        });
      }

      // If it's a boolean question, check its answer and follow the appropriate path
      if (question.type === 'boolean' && currentData) {
        const answer = currentData[question.field];

        // Follow the path based on the answer
        if (answer === true && question.onYes) {
          walkQuestions(question.onYes, depth + 1);
        } else if (answer === false && question.onNo) {
          walkQuestions(question.onNo, depth + 1);
        }
      }
      // If it's a manager decision, follow based on answer
      else if (question.type === 'manager_decision' && currentData) {
        const answer = currentData[question.field];

        if (answer === true && question.onYes) {
          walkQuestions(question.onYes, depth + 1);
        } else if (answer === false && question.onNo) {
          walkQuestions(question.onNo, depth + 1);
        }
      }
      // For other question types, follow the 'next' path
      else if (question.next) {
        walkQuestions(question.next, depth + 1);
      }
    };

    // Start walking from the target question
    walkQuestions(startQuestionId);

    return allowedQuestions;
  }

  /**
   * Get the first question in a loop for a section
   * @param {string} sectionKey - Section key
   * @returns {string} Question ID
   */
  getLoopStartQuestion(sectionKey) {
    const section = this.getSection(sectionKey);
    if (!section) return null;

    // Find the loop start question for this section type
    const loopStartQuestions = {
      'ponds': 'pond_name',
      'curb': 'curb_type_name',
      'walls': 'wall_name',
      'ada-ramps': 'ada_ramp_type'
    };

    return loopStartQuestions[sectionKey] || null;
  }

  /**
   * Check if section is looped or single
   * @param {string} sectionKey - Section key
   * @returns {boolean} True if looped section
   */
  isLoopedSection(sectionKey) {
    const section = this.getSection(sectionKey);
    return section?.type === 'looped';
  }

  /**
   * Get section progress
   * @param {string} sectionKey - Section key
   * @param {Object} sectionData - Current section data
   * @returns {Object} { total, completed, percentage }
   */
  getSectionProgress(sectionKey, sectionData) {
    const section = this.getSection(sectionKey);
    if (!section) return { total: 0, completed: 0, percentage: 0 };

    const questions = section.questions;
    const totalQuestions = Object.keys(questions).length;

    // Count answered questions
    let completedQuestions = 0;
    for (const questionId in questions) {
      const question = questions[questionId];
      if (question.field && sectionData && sectionData[question.field] !== undefined && sectionData[question.field] !== null) {
        completedQuestions++;
      }
    }

    const percentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;

    return {
      total: totalQuestions,
      completed: completedQuestions,
      percentage
    };
  }

  /**
   * Get next section key
   * @param {string} currentSectionKey - Current section
   * @returns {string|null} Next section key or null if last
   */
  getNextSection(currentSectionKey) {
    const keys = this.getSectionKeys();
    const currentIndex = keys.indexOf(currentSectionKey);
    if (currentIndex === -1 || currentIndex === keys.length - 1) {
      return null;
    }
    return keys[currentIndex + 1];
  }

  /**
   * Get previous section key
   * @param {string} currentSectionKey - Current section
   * @returns {string|null} Previous section key or null if first
   */
  getPreviousSection(currentSectionKey) {
    const keys = this.getSectionKeys();
    const currentIndex = keys.indexOf(currentSectionKey);
    if (currentIndex <= 0) {
      return null;
    }
    return keys[currentIndex - 1];
  }

  /**
   * Get answered questions in flow order for a completed item
   * @param {string} sectionKey - Section key
   * @param {Object} itemData - Item data with answers
   * @returns {Array} Array of {question, answer} objects in order
   */
  getAnsweredQuestionsInOrder(sectionKey, itemData) {
    const section = this.getSection(sectionKey);
    if (!section) return [];

    const answeredQuestions = [];

    // Determine starting question based on section type
    let startQuestionId;
    if (section.type === 'looped') {
      startQuestionId = this.getLoopStartQuestion(sectionKey);
    } else {
      // For single sections, start from the initial question
      startQuestionId = section.initialQuestion;
    }

    // Start walking from the start question
    let currentQuestionId = startQuestionId;
    const visited = new Set(); // Prevent infinite loops

    while (currentQuestionId && !visited.has(currentQuestionId)) {
      visited.add(currentQuestionId);
      const question = section.questions[currentQuestionId];

      if (!question) break;

      // Stop if we hit loop control commands or section complete
      if (currentQuestionId === 'LOOP_END' || currentQuestionId === 'LOOP_START' ||
          currentQuestionId === 'SECTION_COMPLETE' || currentQuestionId === 'ADA_LOOP_START' ||
          currentQuestionId === 'ADA_LOOP_END') {
        break;
      }

      // Determine next question based on question type and answer
      let nextQuestionId = null;

      if (question.type === 'boolean' || question.type === 'manager_decision') {
        const answer = itemData[question.field];

        // Only add to answered questions if the field has a value
        if (question.field && answer !== undefined && answer !== null) {
          answeredQuestions.push({
            question: question,
            answer: answer
          });
        }

        // Follow the branching path based on the answer
        if (answer === true && question.onYes) {
          nextQuestionId = question.onYes;
        } else if (answer === false && question.onNo) {
          nextQuestionId = question.onNo;
        } else if (question.next) {
          nextQuestionId = question.next;
        }
      } else if (question.type === 'count') {
        // Count questions lead to loops, but we're viewing a single item
        // Skip count questions in the answered list since they're for navigation
        nextQuestionId = question.next;
      } else {
        // For other types (text, elevation, image, choice, sheet_number, etc.)
        // Only add if this question has an answer
        if (question.field && itemData[question.field] !== undefined && itemData[question.field] !== null) {
          answeredQuestions.push({
            question: question,
            answer: itemData[question.field]
          });
        }

        // Follow next
        nextQuestionId = question.next;
      }

      currentQuestionId = nextQuestionId;
    }

    return answeredQuestions;
  }

  /**
   * Get all field names in a conditional path starting from a question
   * Used to clear stale data when boolean answers change
   * Recursively collects fields from nested boolean paths
   * @param {string} sectionKey - Section key
   * @param {string} startQuestionId - Starting question ID (e.g., from onYes path)
   * @param {string} convergenceQuestionId - Where to stop (e.g., from onNo path or next after boolean)
   * @param {Set} visited - Set of already visited question IDs (for recursion)
   * @returns {Array<string>} Array of field names in this path
   */
  getFieldsInPath(sectionKey, startQuestionId, convergenceQuestionId = null, visited = new Set()) {
    const section = this.getSection(sectionKey);
    if (!section) return [];

    // If we're starting at a convergence point or terminal, return empty
    if (startQuestionId === convergenceQuestionId) return [];
    if (['LOOP_END', 'LOOP_START', 'SECTION_COMPLETE', 'ADA_LOOP_START', 'ADA_LOOP_END'].includes(startQuestionId)) {
      return [];
    }
    if (!startQuestionId) return [];

    const fields = [];
    let currentQuestionId = startQuestionId;

    while (currentQuestionId && !visited.has(currentQuestionId)) {
      visited.add(currentQuestionId);

      // Stop if we hit the convergence point
      if (currentQuestionId === convergenceQuestionId) break;

      const question = section.questions[currentQuestionId];
      if (!question) break;

      // Stop at terminals or confirmations
      if (['LOOP_END', 'LOOP_START', 'SECTION_COMPLETE', 'ADA_LOOP_START', 'ADA_LOOP_END'].includes(currentQuestionId)) {
        break;
      }
      if (question.type === 'confirmation' || currentQuestionId.includes('confirmation')) {
        break;
      }

      // Collect field(s) from this question
      if (question.field && question.type !== 'count') {
        fields.push(question.field);
      }

      // For grouped forms, collect all fields
      if (question.type === 'grouped_form' && question.fields) {
        question.fields.forEach(f => {
          if (f.field) fields.push(f.field);
        });
      }

      // Handle nested boolean questions - recursively collect fields from both paths
      if ((question.type === 'boolean' || question.type === 'manager_decision') && (question.onYes || question.onNo)) {
        // Collect fields from onYes path
        if (question.onYes) {
          const yesFields = this.getFieldsInPath(sectionKey, question.onYes, convergenceQuestionId, visited);
          fields.push(...yesFields);
        }
        // Collect fields from onNo path
        if (question.onNo) {
          const noFields = this.getFieldsInPath(sectionKey, question.onNo, convergenceQuestionId, visited);
          fields.push(...noFields);
        }
        // Continue with 'next' if it exists
        currentQuestionId = question.next;
      } else {
        // For non-boolean questions, just follow next
        currentQuestionId = question.next;
      }
    }

    return fields;
  }

  /**
   * Find the next unanswered question based on existing data
   * @param {string} sectionKey - Section key
   * @param {Object} itemData - Existing item data
   * @returns {Object|null} { questionId, answeredQuestions } or null if complete
   */
  findNextUnansweredQuestion(sectionKey, itemData) {
    const section = this.getSection(sectionKey);
    if (!section) return null;

    const answeredQuestions = [];

    // Determine starting question based on section type
    let startQuestionId;
    if (section.type === 'looped') {
      startQuestionId = this.getLoopStartQuestion(sectionKey);
    } else {
      startQuestionId = section.initialQuestion;
    }

    // Start walking from the start question
    let currentQuestionId = startQuestionId;
    const visited = new Set();

    while (currentQuestionId && !visited.has(currentQuestionId)) {
      visited.add(currentQuestionId);
      const question = section.questions[currentQuestionId];

      if (!question) break;

      // Stop if we hit loop control commands or section complete
      if (currentQuestionId === 'LOOP_END' || currentQuestionId === 'LOOP_START' ||
          currentQuestionId === 'SECTION_COMPLETE' || currentQuestionId === 'ADA_LOOP_START' ||
          currentQuestionId === 'ADA_LOOP_END' || currentQuestionId === 'confirmation' ||
          question.type === 'confirmation') {
        break;
      }

      // Check if this question has been answered
      let isAnswered = false;
      let nextQuestionId = null;

      if (question.type === 'boolean' || question.type === 'manager_decision') {
        const answer = itemData[question.field];
        isAnswered = (answer !== undefined && answer !== null);

        if (isAnswered) {
          answeredQuestions.push({ question, answer });
          // Follow branching path
          if (answer === true && question.onYes) {
            nextQuestionId = question.onYes;
          } else if (answer === false && question.onNo) {
            nextQuestionId = question.onNo;
          } else if (question.next) {
            nextQuestionId = question.next;
          }
        } else {
          // This question is unanswered - return it
          return { questionId: currentQuestionId, answeredQuestions };
        }
      } else if (question.type === 'grouped_form') {
        // Check if any field in the grouped form has data
        const hasAnyData = question.fields?.some(f =>
          itemData[f.field] !== undefined && itemData[f.field] !== null
        );

        if (hasAnyData) {
          // Consider it answered if any field has data
          answeredQuestions.push({ question, answer: itemData });
          nextQuestionId = question.next;
        } else {
          // First unanswered question
          return { questionId: currentQuestionId, answeredQuestions };
        }
      } else if (question.type === 'count') {
        // Skip count questions - they're for navigation
        nextQuestionId = question.next;
      } else if (question.type === 'discrepancy' || question.type === 'info') {
        // These questions don't block - just navigate through
        if (question.field && itemData[question.field]) {
          answeredQuestions.push({ question, answer: itemData[question.field] });
        }
        nextQuestionId = question.next;
      } else {
        // Text, elevation, image, choice, sheet_number, etc.
        const answer = itemData[question.field];
        isAnswered = (answer !== undefined && answer !== null);

        if (isAnswered) {
          answeredQuestions.push({ question, answer });
          nextQuestionId = question.next;
        } else {
          // This is the first unanswered question
          return { questionId: currentQuestionId, answeredQuestions };
        }
      }

      currentQuestionId = nextQuestionId;
    }

    // All questions answered - should show confirmation
    return null;
  }

  /**
   * Get all navigable questions for a section (for navigation dropdown)
   * Excludes control questions like has_curb, how_many_curb_types, etc.
   * @param {string} sectionKey - Section key
   * @returns {Array} Array of {id, text, type} objects for navigable questions
   */
  getNavigableQuestions(sectionKey) {
    const section = this.getSection(sectionKey);
    if (!section) return [];

    const navigableQuestions = [];
    const questions = section.questions;

    // Start from loop start question for looped sections, initial for single sections
    const startQuestionId = section.type === 'looped'
      ? this.getLoopStartQuestion(sectionKey)
      : section.initialQuestion;

    // Walk through questions and collect user-facing questions
    let currentQuestionId = startQuestionId;
    const visited = new Set();

    while (currentQuestionId && !visited.has(currentQuestionId)) {
      visited.add(currentQuestionId);
      const question = questions[currentQuestionId];

      if (!question) break;

      // Stop at control commands
      if (['LOOP_END', 'LOOP_START', 'SECTION_COMPLETE', 'ADA_LOOP_START', 'ADA_LOOP_END'].includes(currentQuestionId)) {
        break;
      }

      // Skip confirmation questions (they're handled separately)
      if (question.type === 'confirmation') {
        break;
      }

      // Include questions that have meaningful content
      // Skip initial gate questions like "has_curb" or "how_many_curb_types"
      const isGateQuestion = question.type === 'boolean' && question.field === null;
      const isCountQuestion = question.type === 'count';

      if (!isGateQuestion && !isCountQuestion) {
        navigableQuestions.push({
          id: question.id,
          text: question.text || question.parentQuestion || question.id,
          type: question.type
        });
      }

      // Move to next question (follow the default path)
      if (question.next) {
        currentQuestionId = question.next;
      } else if (question.onYes) {
        currentQuestionId = question.onYes;
      } else if (question.onNo) {
        currentQuestionId = question.onNo;
      } else {
        break;
      }
    }

    return navigableQuestions;
  }
}

export default WizardEngine;
